import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert, Badge, Button, ButtonGroup, Card, CardBody, Col, Form, FormGroup,
  Input, Label, Row,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { isMtffProxyEnabled } from '../../mtff/mtffClient';
import {
  useInvalidateCache,
  useMtffAudit,
  useMtffInstance,
  useRevalidateWordpress,
  useWarmup,
} from './useMtff';

/**
 * MTFF Ops pro Instance — kombiniert Audit-Log und die drei Cache/Content-
 * Aktionen (Tags-Invalidate / WP-Revalidate / Warmup) auf einer Seite, weil
 * sie in der täglichen Arbeit eng zusammenliegen.
 *
 * Spec: MTFF/docs/ORBIT-INTEGRATION.md §3.6 + §3.7.
 */

export function MtffOpsPage() {
  const { instanceId } = useParams();
  const enabled = isMtffProxyEnabled();
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const instance = useMtffInstance(instanceId);

  if (!enabled) {
    return (
      <div>
        <PageHeader title="MTFF · Ops" />
        <Alert color="warning" className="small">MTFF-Admin-Proxy nicht konfiguriert.</Alert>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`MTFF · Ops · ${instance.data?.tenantId ?? '…'}/${instance.data?.instanceId ?? '…'}`}
        subtitle="Audit-Log + Cache/Content-Aktionen"
        actions={
          <Button color="secondary" outline size="sm" tag={Link} to="/admin/settings/mtff/instances">
            ← Instances
          </Button>
        }
      />

      <Row className="g-3 mb-3">
        <Col lg="4">
          <CacheInvalidateCard instanceId={instanceId} canWrite={canWrite} />
        </Col>
        <Col lg="4">
          <RevalidateCard instanceId={instanceId} canWrite={canWrite} />
        </Col>
        <Col lg="4">
          <WarmupCard instanceId={instanceId} canWrite={canWrite} />
        </Col>
      </Row>

      <AuditLogCard instanceId={instanceId} />
    </div>
  );
}

/* ── Cache + Content ───────────────────────────────────────────────── */

function CacheInvalidateCard({ instanceId, canWrite }) {
  const inv = useInvalidateCache();
  const [text, setText] = useState('home,header');

  const onSubmit = (e) => {
    e.preventDefault();
    const tags = text.split(',').map((s) => s.trim()).filter(Boolean);
    if (tags.length === 0) return;
    inv.mutate({ instanceId, tags });
  };

  return (
    <Card><CardBody>
      <h6 className="text-uppercase small text-muted mb-2">Cache-Tags droppen</h6>
      <Form onSubmit={onSubmit}>
        <FormGroup className="mb-2">
          <Label className="small">Tags (kommagetrennt)</Label>
          <Input
            type="text" bsSize="sm" value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="home, header, footer"
            disabled={!canWrite}
          />
        </FormGroup>
        <HasPermission perms={['integrations.write']}>
          <Button color="primary" size="sm" type="submit" disabled={!canWrite || inv.isPending}>
            {inv.isPending ? 'Droppe…' : '🗑 Tags droppen'}
          </Button>
        </HasPermission>
      </Form>
      <p className="small text-muted mt-2 mb-0">
        Next.js drop pro Tag. Reicht für gezielte Invalidations ohne
        ganze Page neu zu publishen.
      </p>
    </CardBody></Card>
  );
}

function RevalidateCard({ instanceId, canWrite }) {
  const rev = useRevalidateWordpress();
  return (
    <Card><CardBody>
      <h6 className="text-uppercase small text-muted mb-2">WordPress-Revalidate</h6>
      <p className="small text-muted">
        Re-issued den WP-Webhook für diese Instance — vollständiger Cache-
        Purge gegen den konfigurierten WordPress-Source.
      </p>
      <HasPermission perms={['integrations.write']}>
        <Button color="warning" size="sm" onClick={() => rev.mutate({ instanceId })}
          disabled={!canWrite || rev.isPending}>
          {rev.isPending ? 'Triggere…' : '↻ Vollpurge auslösen'}
        </Button>
      </HasPermission>
    </CardBody></Card>
  );
}

function WarmupCard({ instanceId, canWrite }) {
  const wm = useWarmup();
  return (
    <Card><CardBody>
      <h6 className="text-uppercase small text-muted mb-2">Warmup</h6>
      <p className="small text-muted">
        Walked die Sitemap und prefetched die Top-N-Pages nach einem
        Publish — verkürzt TTFB für Customer.
      </p>
      <HasPermission perms={['integrations.write']}>
        <Button color="info" size="sm" onClick={() => wm.mutate({ instanceId })}
          disabled={!canWrite || wm.isPending}>
          {wm.isPending ? 'Wärme auf…' : '🔥 Warmup starten'}
        </Button>
      </HasPermission>
    </CardBody></Card>
  );
}

/* ── Audit-Log ─────────────────────────────────────────────────────── */

function AuditLogCard({ instanceId }) {
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [cursors, setCursors] = useState(['']); // stack für Back-Navigation
  const currentCursor = cursors[cursors.length - 1];
  const query = useMtffAudit(instanceId, { cursor: currentCursor, action, actorUserId: actor });
  const items = query.data?.items ?? [];

  const onNext = () => {
    if (query.data?.nextCursor) setCursors((c) => [...c, query.data.nextCursor]);
  };
  const onPrev = () => {
    setCursors((c) => (c.length > 1 ? c.slice(0, -1) : c));
  };

  return (
    <Card><CardBody>
      <div className="d-flex justify-content-between align-items-end mb-2 flex-wrap gap-2">
        <h6 className="text-uppercase small text-muted mb-0">Audit-Log</h6>
        <div className="d-flex gap-2 align-items-end">
          <FormGroup className="mb-0">
            <Label className="small">Action</Label>
            <Input
              type="text" bsSize="sm" value={action}
              onChange={(e) => { setAction(e.target.value); setCursors(['']); }}
              placeholder="z.B. theme.published"
              style={{ minWidth: 180 }}
            />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label className="small">Actor</Label>
            <Input
              type="text" bsSize="sm" value={actor}
              onChange={(e) => { setActor(e.target.value); setCursors(['']); }}
              placeholder="orbit-user-42"
              style={{ minWidth: 160 }}
            />
          </FormGroup>
        </div>
      </div>
      <DataTable
        loading={query.isLoading}
        empty={!query.isLoading && items.length === 0}
        emptyMessage="Keine Audit-Einträge im Fenster."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 100 }}>Audit-ID</th>
            <th>Action</th>
            <th style={{ width: 180 }}>Actor</th>
            <th>Target</th>
            <th style={{ width: 150 }}>Zeit</th>
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.id}>
              <td className="small"><code>#{e.id}</code></td>
              <td><Badge color="secondary" pill className="orbit-code-badge-sm">{e.action}</Badge></td>
              <td className="small">{e.actorUserId ?? e.actor ?? '—'}</td>
              <td className="small">{e.target ?? '—'}</td>
              <td>{e.occurredAt ? <RelativeTime iso={e.occurredAt} /> : '—'}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      <ButtonGroup size="sm" className="mt-2">
        <Button color="secondary" outline onClick={onPrev} disabled={cursors.length <= 1}>
          ← Zurück
        </Button>
        <Button color="secondary" outline onClick={onNext} disabled={!query.data?.nextCursor}>
          Weiter →
        </Button>
      </ButtonGroup>
    </CardBody></Card>
  );
}
